import { Box, CircularProgress, Flex, styled } from '@shared/components';
import { type ElementRef, forwardRef, useImperativeHandle, useRef } from 'react';

import { RichTextEditorProvider } from '../../providers';
import type { ControlsInput, ImageUploadFunction } from '../../types';
import { RichTextEditorBoxWrapper } from '../rich-text-editor-box-wrapper';
import { RichTextEditorContent } from '../rich-text-editor-content';
import { CONTROLS } from '../rich-text-editor-control';
import { RichTextEditorControlGroup } from '../rich-text-editor-control-group';
import { RichTextEditorToolbar } from '../rich-text-editor-toolbar';

export interface RichTextEditorProps {
  /**
   * The controls of the rich text editor
   *
   * @default [['bold', 'italic', 'underline'], ['alignLeft', 'alignCenter', 'alignRight', 'subscript', 'superscript'], ['bulletList', 'numberList'], ['image', 'table', 'latex', 'beta', 'sd', 'summation']]
   */
  controls?: ControlsInput;
  /**
   * The callback function when the editor is blurred
   */
  onBlur?: () => void;
  /**
   * The callback function when the editor is changed
   */
  onChange?: (value: string) => void;
  /**
   * If true, the editor's border will become red
   */
  error?: boolean;
  /**
   * The callback function when the image is uploaded
   */
  onImageUpload?: ImageUploadFunction;
  /**
   * If true, the editor will show a loading overlay
   */
  loading?: boolean;
  /**
   * The placeholder of the editor
   */
  placeholder?: string;
  /**
   * The value of the editor
   */
  value?: string;
  /**
   * The number of rows to control the height of the editor
   *
   * @default 1
   * */
  rows?: number;
}

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: theme.zIndex.modal,
  width: '100%',
  height: '100%',
  cursor: 'progress',
}));

function renderControls(controls: NonNullable<RichTextEditorProps['controls']>) {
  return controls.map((control) => {
    if (Array.isArray(control)) {
      return (
        <RichTextEditorControlGroup key={`control-group-${control}`}>
          {control.map((groupControl) => {
            const Control = CONTROLS[groupControl];
            return <Control key={groupControl} />;
          })}
        </RichTextEditorControlGroup>
      );
    }
    const Control = CONTROLS[control];
    return <Control key={`single-control-${control}`} />;
  });
}

const DEFAULT_CONTROLS: RichTextEditorProps['controls'] = [
  ['bold', 'italic', 'underline'],
  ['alignLeft', 'alignCenter', 'alignRight', 'subscript', 'superscript'],
  ['bulletList', 'numberList'],
  ['image', 'table', 'latex', 'beta', 'sd', 'summation'],
];

const RichTextEditor = forwardRef<unknown, RichTextEditorProps>(
  (props: RichTextEditorProps, forwardedRef) => {
    const {
      controls = DEFAULT_CONTROLS,
      onBlur,
      onChange,
      error: isError,
      onImageUpload,
      loading: isLoading,
      placeholder,
      value = '',
      rows = 1,
    } = props;
    const editorRef = useRef<ElementRef<typeof RichTextEditorContent>>(null);
    useImperativeHandle(forwardedRef, () => editorRef.current);

    return (
      <RichTextEditorProvider
        onImageUpload={onImageUpload}
        placeholder={placeholder}
        content={value}
        onChange={onChange}
        controls={controls}
      >
        <RichTextEditorBoxWrapper error={isError}>
          {isLoading ? (
            <>
              {/* NOTE: Consider changing this into MUI's backdrop component */}
              <LoadingOverlay />
              <Flex
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: (theme) => theme.zIndex.modal + 1,
                }}
              >
                <CircularProgress />
              </Flex>
            </>
          ) : null}
          <RichTextEditorToolbar>{renderControls(controls)}</RichTextEditorToolbar>
          <RichTextEditorContent ref={editorRef} onBlur={onBlur} value={value} rows={rows} />
        </RichTextEditorBoxWrapper>
      </RichTextEditorProvider>
    );
  },
);

RichTextEditor.displayName = 'RichTextEditor';

export { RichTextEditor };
